/**
 * Provides a caching mechanism using session storage.
 * @returns {Object} Methods to interact with the cache.
 */
export function cacheAdapter(config = { ttl: 600, suspense: 10 }) {
  function getCache(key) {
    const storageKey = `sia.cache.${key}`;
    let cacheValue;
    try {
      cacheValue = JSON.parse(sessionStorage.getItem(storageKey));
    } catch (error) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    if (!cacheValue || cacheValue.data == null) return null;
    if (Date.now() > new Date(cacheValue.expiry).getTime()) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return cacheValue.data;
  }

  function setCache(key, value) {
    const expiry = new Date();
    expiry.setSeconds(expiry.getSeconds() + config.ttl);
    sessionStorage.setItem(`sia.cache.${key}`, JSON.stringify({ expiry, data: value }));
  }

  function clearCache(specificKey) {
    if (specificKey !== undefined && specificKey !== null) {
      sessionStorage.removeItem(`sia.cache.${specificKey}`);
      return;
    }
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("sia.cache.") && !key.includes("masterKey"))
      .forEach((key) => sessionStorage.removeItem(key));
  }

  const delay = () => new Promise((resolve) => setTimeout(resolve, config.suspense));
  const keyify = (urlString) => new URL(urlString).pathname.split("/").slice(3, 4)[0];
  return { getCache, setCache, clearCache, delay, keyify };
}

const { getCache, setCache, delay } = cacheAdapter();

export async function cacheProvider(cacheKey, fetch, useCache = true) {
  if (useCache) {
    const cachedData = getCache(cacheKey);
    if (cachedData !== null) {
      await delay();
      return cachedData;
    }
  }
  const data = await fetch();
  if (useCache) setCache(cacheKey, data);
  return data;
}
